<template>
  <!-- Card 17trt6jbjf2: abaixo da chamada, o homework do assunto desta aula
       entregue à turma, com quem concluiu e a resposta de cada aluno (retorno
       de 14/10). Homework é opcional para o aluno, então a lista só informa —
       sem tom de cobrança para quem ainda não marcou. As observações por aula
       viraram o "Planejamento de aula" da Visão geral. -->
  <div class="lesson-follow-up mt-8">
    <section class="lesson-follow-up__block">
      <div class="lesson-follow-up__head">
        <v-icon small :color="theme.primary" class="mr-2">
          mdi-notebook-edit-outline
        </v-icon>
        <span class="lesson-follow-up__title">{{
          $t("lessonFollowUp.title")
        }}</span>
        <v-spacer />
        <!-- Card 17trt6jen8u: segunda forma de definir homework, sem ir ao
             material. Turma e assunto vêm da aula; só sem assunto não há o
             botão (o aviso abaixo pede para escolher). -->
        <v-btn
          v-if="courseClassId && students.length"
          small
          rounded
          depressed
          :color="theme.primary"
          class="white--text"
          @click="openDefine"
        >
          <v-icon x-small left>mdi-plus</v-icon>
          {{ $t("lessonFollowUp.define") }}
        </v-btn>
      </div>

      <page-skeleton
        v-if="loadingHomework"
        variant="rows"
        :rows="2"
        class="mt-3"
      />

      <div v-else-if="!courseClassId" class="lesson-follow-up__empty">
        {{ $t("lessonFollowUp.pickSubject") }}
      </div>

      <div v-else-if="homeworkError" class="lesson-follow-up__empty">
        {{ $t("lessonFollowUp.loadFailed") }}
        <v-btn x-small text :color="theme.primary" @click="loadHomework">
          {{ $t("common.tryAgain") }}
        </v-btn>
      </div>

      <div v-else-if="!homework.length" class="lesson-follow-up__empty">
        {{ $t("lessonFollowUp.empty") }}
      </div>

      <template v-else>
        <div v-for="item in homework" :key="item.id" class="homework-card mt-3">
          <linkified-text
            tag="div"
            class="homework-card__text"
            :text="item.note"
          />
          <ul
            v-if="item.files && item.files.length"
            class="homework-card__files"
          >
            <li v-for="file in item.files" :key="file.id">
              <a :href="file.url" target="_blank" rel="noopener">
                <v-icon x-small class="mr-1">mdi-paperclip</v-icon>
                {{ file.name }}
              </a>
            </li>
          </ul>
          <!-- Retorno de 26/10 (card 17trt6jen8u): o professor edita o texto de
               um homework já enviado. -->
          <v-btn
            x-small
            text
            :color="theme.primary"
            class="homework-card__edit"
            @click="openEdit(item)"
          >
            <v-icon x-small left>mdi-pencil-outline</v-icon>
            {{ $t("lessonFollowUp.edit") }}
          </v-btn>
          <div class="homework-card__meta">
            {{
              $t("lessonFollowUp.sentOn", { date: formatDate(item.createdAt) })
            }}
            <template v-if="item.author"> · {{ item.author }}</template>
            <template v-if="item.filesCount">
              ·
              {{
                $tc("common.attachments", item.filesCount, {
                  count: item.filesCount,
                })
              }}
            </template>
          </div>

          <div class="homework-card__summary">
            {{
              $tc("lessonFollowUp.completedSummary", item.sentCount, {
                done: item.completedCount,
                sent: item.sentCount,
              })
            }}
            <template v-if="item.respondedCount">
              ·
              {{
                $tc("lessonFollowUp.responses", item.respondedCount, {
                  count: item.respondedCount,
                })
              }}
            </template>
          </div>

          <div class="homework-card__students">
            <div
              v-for="student in item.students"
              :key="student.customerId"
              class="homework-student"
            >
              <v-avatar
                size="28"
                class="mr-2"
                :color="avatarOf(student.customerId) ? '' : '#e7e3ec'"
              >
                <v-img
                  v-if="avatarOf(student.customerId)"
                  :src="avatarOf(student.customerId)"
                  cover
                />
                <span v-else class="homework-student__initials">
                  {{ initials(student.name) }}
                </span>
              </v-avatar>
              <span class="homework-student__name">
                {{ student.name || $t("common.student") }}
              </span>
              <span
                v-if="student.completed"
                class="homework-student__status homework-student__status--done"
              >
                <v-icon x-small color="#00A379" class="mr-1">
                  mdi-check-circle
                </v-icon>
                {{
                  student.completedAt
                    ? $t("lessonFollowUp.completedOn", {
                        date: formatDate(student.completedAt),
                      })
                    : $t("lessonFollowUp.completed")
                }}
              </span>
              <span v-else class="homework-student__status">
                {{ $t("lessonFollowUp.notCompleted") }}
              </span>
              <div v-if="student.response" class="homework-response">
                <div class="homework-response__meta">
                  {{
                    $t("lessonFollowUp.responseSentOn", {
                      date: formatDateTime(student.response.respondedAt),
                    })
                  }}
                </div>
                <div
                  v-if="student.response.text"
                  class="homework-response__text"
                >
                  {{ student.response.text }}
                </div>
                <a
                  v-if="student.response.file"
                  :href="student.response.file.url"
                  target="_blank"
                  rel="noopener"
                  class="homework-response__file"
                >
                  <v-icon x-small class="mr-1">mdi-paperclip</v-icon>
                  {{ student.response.file.name }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </template>
    </section>

    <v-dialog v-model="editOpen" max-width="520">
      <v-card class="define-homework">
        <v-card-title class="define-homework__title">
          {{ $t("lessonFollowUp.editTitle") }}
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="editDraft"
            :label="$t('lessonFollowUp.text')"
            :counter="maxLength"
            :maxlength="maxLength"
            rows="3"
            auto-grow
            outlined
          />
          <div class="define-homework__subject">
            {{ $t("lessonFollowUp.editNote") }}
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer />
          <v-btn text :disabled="savingEdit" @click="editOpen = false">
            {{ $t("common.cancel") }}
          </v-btn>
          <v-btn
            rounded
            depressed
            :color="theme.primary"
            class="white--text"
            :disabled="!canSaveEdit"
            :loading="savingEdit"
            @click="saveEdit"
          >
            {{ $t("lessonFollowUp.saveEdit") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="defineOpen" max-width="520">
      <v-card class="define-homework">
        <v-card-title class="define-homework__title">
          {{ $t("lessonFollowUp.defineTitle") }}
        </v-card-title>
        <v-card-text>
          <div class="define-homework__subject">
            <v-icon x-small class="mr-1">mdi-book-open-variant</v-icon>
            {{
              subjectTitle
                ? $t("lessonFollowUp.subject", { title: subjectTitle })
                : $t("lessonFollowUp.subjectOfLesson")
            }}
          </div>

          <div class="define-homework__label mt-4">
            {{ $t("lessonFollowUp.pickStudents") }}
          </div>
          <v-checkbox
            v-if="students.length > 1"
            :input-value="allSelected"
            :indeterminate="someSelected"
            :label="$t('lessonFollowUp.allStudents')"
            :color="theme.primary"
            hide-details
            dense
            class="mt-1"
            @change="toggleAll"
          />
          <v-checkbox
            v-for="student in students"
            :key="student.id"
            v-model="selectedIds"
            :value="student.id"
            :label="student.name || $t('common.student')"
            :color="theme.primary"
            hide-details
            dense
            class="mt-1"
            :class="{ 'ml-6': students.length > 1 }"
          />

          <v-textarea
            v-model="draft"
            :label="$t('lessonFollowUp.text')"
            :placeholder="$t('lessonFollowUp.textPlaceholder')"
            :counter="maxLength"
            :maxlength="maxLength"
            rows="3"
            auto-grow
            outlined
            class="mt-5"
          />

          <!-- Retorno de 27/10: anexos no homework, com os mesmos limites do
               material. Sobem na seleção, para o erro de tipo/tamanho aparecer
               antes de o professor escrever tudo. -->
          <div class="define-homework__attach">
            <v-btn
              small
              outlined
              rounded
              :color="theme.primary"
              :loading="uploading"
              :disabled="uploading || draftFiles.length >= maxFiles"
              @click="pickFiles"
            >
              <v-icon small left>mdi-paperclip</v-icon>
              {{ $t("teacherNotes.attach") }}
            </v-btn>
            <span class="define-homework__hint">
              {{
                $t("teacherNotes.attachHint", {
                  count: maxFiles,
                  size: maxFileSizeMb,
                })
              }}
            </span>
            <input
              ref="fileInput"
              type="file"
              class="define-homework__input"
              :accept="acceptedTypes"
              @change="onFilesPicked"
            />
            <div v-if="draftFiles.length" class="define-homework__files">
              <v-chip
                v-for="file in draftFiles"
                :key="file.id"
                small
                close
                @click:close="removeDraftFile(file)"
              >
                {{ file.name }}
              </v-chip>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer />
          <v-btn text :disabled="sending" @click="defineOpen = false">
            {{ $t("common.cancel") }}
          </v-btn>
          <v-btn
            rounded
            depressed
            :color="theme.primary"
            class="white--text"
            :disabled="!canSend"
            :loading="sending"
            @click="sendHomework"
          >
            {{ $t("lessonFollowUp.send") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import { initialsFromName } from "@/shared/classPresentation";
import { languageTag } from "@/plugins/i18n";
import LinkifiedText from "@/components/course/LinkifiedText.vue";

// Anexos do homework: mesmos limites do material (PARTNER_CLASS_NOTE_MAX_FILES
// e 10 MB na API; "pdf, doc ou jpg").
const HOMEWORK_MAX_FILES = 5;
const HOMEWORK_MAX_FILE_SIZE_MB = 10;
const HOMEWORK_ACCEPTED_TYPES = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".gif",
  ".doc",
  ".docx",
].join(",");

// Datas aqui são instantes reais (envio, conclusão, edição), não horário de
// parede de aula: formatamos no fuso da escola.
const SCHOOL_TIME_ZONE = "America/Sao_Paulo";

// Mesmo teto da anotação na API (PARTNER_CLASS_NOTE_MAX_LENGTH).
const HOMEWORK_MAX_LENGTH = 500;

export default {
  name: "ClassLessonFollowUp",

  components: { LinkifiedText },

  props: {
    groupId: {
      type: [String, Number],
      required: true,
    },
    // ids de evento são bigint: sempre string, nunca Number().
    eventId: {
      type: String,
      required: true,
    },
    // Assunto gravado no evento. Muda quando o professor troca o assunto na
    // própria aba, e o homework precisa acompanhar.
    courseClassId: {
      type: String,
      default: null,
    },
    subjectTitle: {
      type: String,
      default: "",
    },
    members: {
      type: Array,
      default: () => [],
    },
    // Alunos desta aula: a quem o homework pode ser endereçado.
    students: {
      type: Array,
      default: () => [],
    },
    theme: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      homework: [],
      loadingHomework: false,
      homeworkError: false,
      // Respostas que chegam fora de ordem (troca rápida de aula) não podem
      // sobrescrever a aula atual.
      homeworkRequest: 0,
      defineOpen: false,
      selectedIds: [],
      draft: "",
      draftFiles: [],
      uploading: false,
      maxFiles: HOMEWORK_MAX_FILES,
      maxFileSizeMb: HOMEWORK_MAX_FILE_SIZE_MB,
      acceptedTypes: HOMEWORK_ACCEPTED_TYPES,
      sending: false,
      editOpen: false,
      editId: null,
      editDraft: "",
      savingEdit: false,
      maxLength: HOMEWORK_MAX_LENGTH,
    };
  },

  computed: {
    allSelected() {
      return (
        this.students.length > 0 &&
        this.selectedIds.length === this.students.length
      );
    },
    someSelected() {
      return this.selectedIds.length > 0 && !this.allSelected;
    },
    canSend() {
      return (
        !this.sending &&
        !this.uploading &&
        Boolean(this.courseClassId) &&
        this.selectedIds.length > 0 &&
        Boolean(this.draft.trim())
      );
    },
    canSaveEdit() {
      const original = this.homework.find((item) => item.id === this.editId);
      const text = this.editDraft.trim();
      return (
        !this.savingEdit && Boolean(text) && text !== (original?.note || "")
      );
    },
    avatarsById() {
      return new Map(
        this.members.map((member) => [member.id, member.avatar || null])
      );
    },
  },

  watch: {
    eventId: {
      immediate: true,
      handler() {
        this.loadHomework();
      },
    },
    courseClassId() {
      this.loadHomework();
    },
  },

  methods: {
    async loadHomework() {
      const request = ++this.homeworkRequest;

      this.homework = [];
      this.homeworkError = false;

      if (!this.courseClassId) {
        this.loadingHomework = false;
        return;
      }

      this.loadingHomework = true;

      try {
        const res = await partnerApi.getLessonHomework(
          this.groupId,
          this.eventId
        );

        if (request !== this.homeworkRequest) return;

        this.homework = Array.isArray(res.data?.homework)
          ? res.data.homework
          : [];
      } catch (error) {
        if (request !== this.homeworkRequest) return;
        this.homeworkError = true;
      } finally {
        if (request === this.homeworkRequest) {
          this.loadingHomework = false;
        }
      }
    },
    // Card 17trt6jen8u: o professor escolhe o aluno; com um aluno só, ele já
    // vem marcado.
    openDefine() {
      this.draft = "";
      this.draftFiles = [];
      this.selectedIds =
        this.students.length === 1 ? [this.students[0].id] : [];
      this.defineOpen = true;
    },
    openEdit(item) {
      this.editId = item.id;
      this.editDraft = item.note || "";
      this.editOpen = true;
    },
    // Só o texto muda; os anexos e quem já recebeu ficam como estão. A edição
    // não gera novo aviso ao aluno (a API não notifica).
    async saveEdit() {
      if (!this.canSaveEdit) return;
      this.savingEdit = true;
      try {
        await partnerApi.updateCourseNote(this.editId, this.editDraft.trim());
        this.editOpen = false;
        this.$root.$emit("display-alert", {
          text: this.$t("lessonFollowUp.edited"),
          type: 0,
        });
        this.loadHomework();
      } catch (error) {
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message ||
            this.$t("lessonFollowUp.editFailed"),
          type: 1,
        });
      } finally {
        this.savingEdit = false;
      }
    },
    alert(text, type = 1) {
      this.$root.$emit("display-alert", { text, type });
    },
    pickFiles() {
      this.$refs.fileInput.click();
    },
    // Upload em duas etapas, como no material: a API valida tipo/tamanho e
    // devolve a URL assinada, e o arquivo sobe direto no bucket. O id só vira
    // anexo quando o homework é enviado.
    async onFilesPicked(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";

      if (!file) return;

      if (this.draftFiles.length >= this.maxFiles) {
        this.alert(this.$t("teacherNotes.maxFiles", { count: this.maxFiles }));
        return;
      }

      if (file.size > this.maxFileSizeMb * 1024 * 1024) {
        this.alert(
          this.$t("teacherNotes.tooBig", { size: this.maxFileSizeMb })
        );
        return;
      }

      this.uploading = true;

      try {
        const res = await partnerApi.requestMaterialUploadUrl(file);
        await partnerApi.uploadMaterialToSignedUrl(res.data.url, file);
        this.draftFiles.push({ id: res.data.fileId, name: file.name });
      } catch (error) {
        const message = error?.response?.data?.message;
        this.alert(
          typeof message === "string" && message
            ? message
            : this.$t("teacherNotes.uploadFailed")
        );
      } finally {
        this.uploading = false;
      }
    },
    removeDraftFile(file) {
      this.draftFiles = this.draftFiles.filter((item) => item.id !== file.id);
    },
    toggleAll(checked) {
      this.selectedIds = checked
        ? this.students.map((student) => student.id)
        : [];
    },
    // Mesmo caminho do homework criado no material: anotação do tipo homework
    // no assunto da aula, já entregue à turma e aos alunos escolhidos (eles
    // recebem a notificação de homework).
    async sendHomework() {
      if (!this.canSend) return;
      this.sending = true;
      try {
        await partnerApi.createCourseNote(this.courseClassId, {
          note: this.draft.trim(),
          kind: "homework",
          fileIds: this.draftFiles.map((file) => file.id),
          share: {
            classId: String(this.groupId),
            customerIds: this.selectedIds,
          },
        });
        this.defineOpen = false;
        this.$root.$emit("display-alert", {
          text: this.$t("lessonFollowUp.sent"),
          type: 0,
        });
        this.loadHomework();
      } catch (error) {
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message ||
            this.$t("lessonFollowUp.sendFailed"),
          type: 1,
        });
      } finally {
        this.sending = false;
      }
    },
    avatarOf(customerId) {
      return this.avatarsById.get(customerId) || null;
    },
    initials(name) {
      return initialsFromName(name);
    },
    formatDate(value) {
      const date = value ? new Date(value) : null;

      if (!date || Number.isNaN(date.getTime())) {
        return "-";
      }

      return new Intl.DateTimeFormat(languageTag(), {
        day: "2-digit",
        month: "2-digit",
        timeZone: SCHOOL_TIME_ZONE,
      }).format(date);
    },
    formatDateTime(value) {
      const date = value ? new Date(value) : null;

      if (!date || Number.isNaN(date.getTime())) {
        return "-";
      }

      return new Intl.DateTimeFormat(languageTag(), {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: SCHOOL_TIME_ZONE,
      }).format(date);
    },
  },
};
</script>

<style scoped>
.lesson-follow-up {
  padding-top: 20px;
  border-top: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
}

.lesson-follow-up__head {
  display: flex;
  align-items: center;
}

.lesson-follow-up__title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--dk-ink, #231a2e);
}

/* O diálogo fica fora do wrapper: cores sempre com fallback. */
.define-homework {
  border-radius: 16px !important;
}

.define-homework__title {
  font-weight: 700;
  color: var(--dk-ink, #231a2e);
}

.define-homework__subject {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--dk-paper, #f7f5fa);
  color: var(--dk-ink, #231a2e);
  font-size: 0.85rem;
}

.define-homework__attach {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.define-homework__hint {
  font-size: 0.75rem;
  color: var(--dk-muted, #6b6472);
}

.define-homework__input {
  display: none;
}

.define-homework__files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
}

.define-homework__label {
  font-weight: 600;
  color: var(--dk-ink, #231a2e);
}

.lesson-follow-up__hint,
.lesson-follow-up__muted {
  color: var(--dk-muted, #6b6472);
  font-size: 0.85rem;
}

.lesson-follow-up__hint {
  margin-top: 2px;
}

.lesson-follow-up__empty {
  margin-top: 10px;
  color: var(--dk-muted, #6b6472);
  font-size: 0.9rem;
}

.homework-card {
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  background: var(--dk-surface, #ffffff);
}

.homework-card__text {
  color: var(--dk-ink, #231a2e);
  white-space: pre-line;
  word-break: break-word;
}

.homework-card__files {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.82rem;
}

.homework-card__files a {
  color: var(--dk-ink, #231a2e);
  font-weight: 600;
  word-break: break-all;
}

.homework-card__edit {
  margin: 2px 0 0 -8px;
  letter-spacing: normal;
  text-transform: none;
}

.homework-card__meta {
  margin-top: 4px;
  font-size: 0.78rem;
  color: var(--dk-faint, #94a3b8);
}

.homework-card__summary {
  margin-top: 10px;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--dk-ink, #231a2e);
}

.homework-card__students {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.homework-student {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 0;
}

.homework-student__initials {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--dk-muted, #6b6472);
}

.homework-student__name {
  font-weight: 600;
  color: var(--dk-ink, #231a2e);
  margin-right: 10px;
}

.homework-student__status {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  color: var(--dk-faint, #94a3b8);
}

.homework-student__status--done {
  color: var(--dk-teal-deep, #007f5e);
}

.homework-response {
  flex-basis: 100%;
  margin: 4px 0 6px 36px;
  padding: 8px 12px;
  border-radius: 10px;
  background: var(--dk-paper, #f7f5fa);
}

.homework-response__meta {
  font-size: 0.75rem;
  color: var(--dk-faint, #94a3b8);
}

.homework-response__text {
  margin-top: 2px;
  color: var(--dk-ink, #231a2e);
  font-size: 0.875rem;
  white-space: pre-line;
  word-break: break-word;
}

.homework-response__file {
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  font-size: 0.82rem;
  word-break: break-all;
}
</style>
